import React, { useEffect, useState } from 'react'
import { StepByStepProgress, SurveyQuestion, PersonalData } from '../components/Survey'
import { useStateContext } from '../context/StateContext'
import { useAuthContext } from '../whitaijiFrontendLibrary/auth/hooks/useAuthContext'
import { AuthContext } from '../context/AuthContext'
import BlueButton from '../components/BlueButton'
import { generateMongoDBId } from '../whitaijiFrontendLibrary/lib/utils'
import { useQuery } from "@tanstack/react-query"
import { _FETCH } from '../utils/patches'
import Loading from "../whitaijiFrontendLibrary/components/Loading"
import T from '../components/Translation'
import { errorAlert, successAlert } from '../utils/utils'
import { _CREATEPUBLIC } from '../utils/patches'
import { useMutation } from '@tanstack/react-query'
import queryClient from '../utils/queryClient';
import { dT } from '../components/Translation'
import { useNavigate } from 'react-router-dom'

const Survey = () => {

    const urlParams = new URLSearchParams(window.location.search);
    const surveyId = urlParams.get('_id');
    const campaignId = urlParams.get('campaignId');
    const emailId = urlParams.get('emailId');
    const email = urlParams.get('userEmail');
    const pageId = "survey";


    const {apiUrl, language} = useStateContext()
    const {user} = useAuthContext(AuthContext)
    const {surveyResponseDoc, setSurveyResponseDoc} = useStateContext()
    const [saveError, setSaveError] = useState(null);
    const [emailLogged, setEmailLogged] = useState(false);
    const navigate = useNavigate()

    const { data: survey, error, isLoading } = useQuery({
        queryKey: ['survey', surveyId],
        enabled: !!surveyId && !!apiUrl,
        queryFn: async () => {
            const response = await fetch(`${apiUrl}/surveys/${surveyId}`);
            if (!response.ok) {
                throw new Error('Failed to fetch survey');
            }
            return response.json();
        }
    });
/*     const surveyExample = {
        _id: "ndewkjwenjkd",
        questions:[
            { _id: "wlkdmwlkemd", title: "test Question A", type: "radio", description: "test description1", answerOptions:[{answerId: "woiwechku", answerText: "test answer 1"}, {answerId: "kwllwlwl", answerText: "test answer 2"}]},
            { _id: "powkweicj", title: "test Question 2", type: "multiple", description: "test description2", answerOptions:[{answerId: "kwjkwejdcn", answerText: "test multiple answer 1"}, {answerId: "cojiewoidcnh", answerText: "test multiple answer 2"}]},
            { _id: "lwlkwjecdnòqwj", title: "test Question 3", type: "shortText", description: "test description3"},
            {_id: "wewqwkleiie", title: "test Question 4", type: "longText", description: "test description4"},
        ]
    } */

    /**
     * Initializes a new survey response document when none exists and survey data is available
     * @description Creates a new survey response with default values for user data and responses
     * Types:
     * {
        _id: generateMongoDBId,
        surveyId: string,
        userResponses: array,
        firstName: string,
        lastName: string,
        email: string,
        phoneNumber: 'string,
        country: object,
        city: object,
        acceptPrivacy: bool,
        acceptMarketing: bool
        }
        
     * Example response structure:
     * userResponses: [{
     *   questionId: "wlkdmwlkemd",
     *   answers: [{
     *     answerId: "woiwechku",
     *     answerText: "test answer 1"
     *   }]
     * }]
     * city: {
     *   countryCode: "IT",
     *   label: "Città metropolitana di Milano",
     *   latitude: "45.45186000",
     *   longitude: "9.14586000",
     * }
     * country: {
     *   countryCode: "IT",
     *   isoCode: "IT",
     *   label: "Italy",
     *   latitude: "42.83333333",
     *   longitude: "12.83333333",
     *   timeZone: "Europe/Rome"
     * }
     */
    useEffect(() => {
        if(!surveyResponseDoc?._id && survey?._id) {
            const newSurveyResponseDoc = {
                _id: generateMongoDBId(),
                campaignId: campaignId,
                emailId: emailId,
                surveyId: survey._id,
                userResponses: [],
                firstName: '',
                lastName: '',
                email: email,
                phoneNumber: '',
                professionalTitle: '',
                country: null,
                city: null,
                acceptPrivacy: false,
                acceptMarketing: false
            }
            process.env.NODE_ENV === "development" && console.log("newSurveyResponseDoc", newSurveyResponseDoc)
            setSurveyResponseDoc(newSurveyResponseDoc)
        }
    },[surveyResponseDoc, survey, urlParams])

    //step by step

    const [validationErrors, setValidationErrors] = useState({});
    const [showValidation, setShowValidation] = useState(false);

    const dataToAllComponents = {doc: surveyResponseDoc, setDoc: setSurveyResponseDoc}
    
    const questions = !survey?.questions ? [] : survey?.questions
    const questionsProgressList = questions?.map((item)=>{
        const title = item?.required ? `${item?.title} *` : item?.title
        return ({tabTitle: title, required: item?.required, component: <SurveyQuestion data={{...dataToAllComponents, ...item}}/>})
        })

    const progressList = [
        ...questionsProgressList,
        {tabTitle: <T str="aboutYou" />, component: <PersonalData 
            data={{...dataToAllComponents}} 
            showValidation={showValidation}
            onValidationChange={(hasErrors) => {
                if (!showValidation) {
                    return;
                }
                
                setValidationErrors(prev => {
                    // Start with a clean copy of previous errors, excluding personalData
                    const newErrors = Object.entries(prev)
                        ?.filter(([key]) => key.startsWith('question_'))
                        ?.reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
                    
                    // Only add personalData error if there are actual errors
                    if (hasErrors) {
                        newErrors.personalData = true;
                    }
                    
                    return newErrors;
                });
            }}
        />},        
      ]
   
/*       {tabTitle: "ONE ANSWER", component: <SurveyQuestion doc={surveyDoc} setDoc={save} data={{
        ...dataToAllComponents,
        type:"radio",
        answerOptions:[{answerId: 1, answerText: "test answer 1"}, {answerId: 2, answerText: "test answer 2"}],
        question: {_id: 123, title: "test Question 1"}
    }}/>}, */

    const validateSurveyResponse = () => {
        const errors = {};
        
        // Validate questions
        survey.questions.forEach((question, index) => {
            if (question.required) {
                const response = surveyResponseDoc.userResponses.find(r => r.questionId === question._id);
                const hasAnswer = response?.answers?.some(a => {
                    switch (question.type) {
                        case 'radio':
                            return a.answerId;
                        case 'multiple':
                            return a.answerId;
                        case 'shortText':
                        case 'longText':
                            return a.answerText && a.answerText.trim() !== '';
                        default:
                            return false;
                    }
                });
                if (!response || !hasAnswer) {
                    errors[`question_${index}`] = true;
                }
            }
        });

        return errors;
    };

    // Update validation errors whenever answers change, but only show after first save attempt
    /**
     * Handles survey validation when answers change
     * @description Validates required questions and personal data, but only shows errors after first save attempt
     */
    useEffect(() => {
        // Always start with a clean validation state
        setValidationErrors({});

        // Only proceed with validation if showValidation is true and we have necessary data
        if (showValidation && survey && surveyResponseDoc) {
            const errors = validateSurveyResponse();
            
            // Validate personal data
            const emailRegex = /\S+@\S+\.\S+/;
            const hasPersonalDataErrors = !(
                surveyResponseDoc.firstName?.trim() &&
                surveyResponseDoc.lastName?.trim() &&
                surveyResponseDoc.email?.trim() &&
                emailRegex.test(surveyResponseDoc.email) &&
                surveyResponseDoc.country?.label &&
                surveyResponseDoc.city?.label &&
                surveyResponseDoc.acceptPrivacy
            );

            // Only set validation errors if there are actual errors
            if (Object.keys(errors).length > 0 || hasPersonalDataErrors) {
                setValidationErrors({
                    ...errors,
                    ...(hasPersonalDataErrors ? { personalData: true } : {})
                });
            }
        }
    }, [survey, surveyResponseDoc, showValidation]);
    const resetSurvey = () => {
        setSurveyResponseDoc(null);
        setShowValidation(false);
        setValidationErrors({});
    } 
    const deleteSurveyResponse = () => {
        const message = dT({en: 'Survey deleted successfully!', it: 'Sondaggio cancellato con successo!'}, language);
        setSurveyResponseDoc(undefined);
        setShowValidation(false);
        setValidationErrors({});
        successAlert(message);
        navigate('/')
    }

      const onError = (e) => {
        setSaveError(e.error);
        errorAlert(e.error || 'Failed to save survey');
      }
    
      const onSuccess = (json) => {
        if (json.error) {
          setSaveError(json.error);
          errorAlert(json.error);
          return;
        }
        setSaveError(null);
        const message = dT({en: 'Survey submitted successfully!', it: 'Sondaggio salvato con successo!'}, language);
        successAlert(message);
        resetSurvey()

      }
    const { mutate: saveSurveyResponseToDb, isSurveyLoading } = useMutation({
        mutationFn: async(surveyResponse) => {
          try {
              // Check if survey exists in user's surveys
              //const surveyExists = userSurveys?.some(s => s._id === newSurvey._id);
              
              // Create new survey
              return await _CREATEPUBLIC({
                doc: surveyResponse, 
                route: `${apiUrl}/survey-responses`, 
                onError, 
                onSuccess
              });
            
          } catch (error) {
            onError(error);
            throw error;
          }
        },
        //onSettled: () => queryClient.invalidateQueries({ queryKey: ['surveyResponsesData'] }),
        mutationKey: ['surveyResponsesData'],
      });
        const campaignLog = {
            logType:"emailLink", 
            surveyId, 
            campaignId, 
            emailId, 
            prospectEmail: email, 
            user_id: user?._id, 
            pageId
        }


        const { mutate: marketingLogsData } = useMutation({
        mutationFn: async(doc) => {
          try {
              return await _CREATEPUBLIC({
                doc, 
                route: `${apiUrl}/marketing-logs/public`, 
              });
            
          } catch (error) {
            //onError(error);
            throw error;
          }
        },
        //onSettled: () => queryClient.invalidateQueries({ queryKey: ['surveyResponsesData'] }),
        mutationKey: ['marketingLogsData'],
        onSuccess: () => {
            setEmailLogged(true)
        }
      });
        useEffect(() => {
            if (!emailLogged && urlParams && emailId && emailId !== "undefined") {
                marketingLogsData(campaignLog)
            }
        }, [emailId])
    const saveSurveyResponse = () => {
        //#surveyErrorValidation
        //#surveyResponse
        setShowValidation(true);

        // Validate survey questions
        const errors = validateSurveyResponse();
        
        // Validate personal data
        const emailRegex = /\S+@\S+\.\S+/;
        const hasPersonalDataErrors = !(
            surveyResponseDoc.firstName?.trim() &&
            surveyResponseDoc.lastName?.trim() &&
            surveyResponseDoc.email?.trim() &&
            emailRegex.test(surveyResponseDoc.email) &&
            surveyResponseDoc.country?.label &&
            surveyResponseDoc.city?.label &&
            surveyResponseDoc.acceptPrivacy
        );
        
        // Only proceed if there are no errors
        const hasAnyErrors = Object.keys(errors).length > 0 || hasPersonalDataErrors;
        
        if (!hasAnyErrors) {
            // Clear validation states immediately
            setShowValidation(false);
            setValidationErrors({});
            process.env.NODE_ENV === "development" && console.log("TEST SURVEY RESPONSE (COMPLETED)", surveyResponseDoc);
            saveSurveyResponseToDb(surveyResponseDoc);

            //copyJsonToClipboard(surveyResponseDoc);
        } else {
            // Set validation errors
            setValidationErrors({
                ...errors,
                ...(hasPersonalDataErrors ? { personalData: true } : {})
            });
            process.env.NODE_ENV === "development" && console.log("TEST SURVEY RESPONSE (ERROR)", surveyResponseDoc);
        }
    }
  
  if (isLoading) {
    return <Loading />
  }

  if (error) {
    return <div style={{color: 'red', padding: '30px'}}>{error instanceof Error ? error.message : 'An error occurred'}</div>
  }

  if (!survey || !surveyResponseDoc) {
    return <Loading />
  }

  return (
    <div style={{padding: "30px"}}>
        <div style={{textAlign: 'center', marginTop: '15px', marginBottom: '15px'}}>
         <h2>{survey.title}</h2>
        </div> 
        <BlueButton fn={saveSurveyResponse} color='green' text={<T str="saveSurvey" />}/>
        {error && <div style={{color: 'red'}}>{error}</div>}
        <BlueButton fn={resetSurvey} text={<T str="resetSurvey" />}/>
        <BlueButton fn={deleteSurveyResponse} color="red" text={<T str="deleteSurveyResponse" />}/>
        <StepByStepProgress progressList={progressList} validationErrors={validationErrors}/>
    </div>
  )
}

export default Survey

export const copyJsonToClipboard = (json) => {
    const jsonString = JSON.stringify(json, null, 2);
    navigator.clipboard.writeText(jsonString)
      .then(() => {
        process.env.NODE_ENV === "development" && console.log('JSON copied to clipboard');
      })
      .catch((error) => {
        process.env.NODE_ENV === "development" && console.error('Error copying JSON to clipboard:', error);
      });
  };
